/**
 * Platform-admin controls for the nightly database export.
 *
 * Sits BELOW the Status page's Backups card and does not touch it. The card
 * grades whether the export is still landing (ok/stale/missing/unknown) and
 * that judgement is made server-side so the page cannot disagree with the
 * smoke test; this panel is the other half — starting one, and getting one
 * back out.
 *
 * NEITHER ACTION HAPPENS HERE OR IN FLASK. "Create backup" asks the existing
 * db-backup Cloud Run job to run, because the app deliberately holds no
 * database-export rights; the job's own service account does. "Download"
 * follows a ten-minute signed URL straight to GCS, because games.sql.gz is
 * over a gigabyte and Cloud Run kills a response around 32 MiB.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../lib/api'
// The classes this panel uses live here. It imported NO stylesheet at all, so
// every control fell back to browser defaults — and five of the seven classes
// it named (.btn-quiet, .st-input, .st-h3, .st-table, .st-label, .st-value)
// were never defined anywhere. `.btn` exists only in match-summary.css, which
// the Status page does not load, so even the primary button was unstyled.
import './admin.css'
import './status.css'
import './resiliency.css'

type BackupFile = { name: string; object: string; size: number | null; updated: string | null }
type BackupDay = { date: string; files: BackupFile[]; manifest: BackupFile | null }
type Listing = { backups: BackupDay[]; bucket?: string; reason?: string }
type RunState = {
  state: 'running' | 'succeeded' | 'failed' | 'none' | 'unknown'
  execution?: string
  started_at?: string | null
  completed_at?: string | null
  reason?: string
}

/** Bytes as a person reads them. A dump is always MB or GB here. */
function size(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1 }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—'
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  })
}

export function BackupControls() {
  const [listing, setListing] = useState<Listing | null>(null)
  const [run, setRun] = useState<RunState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadListing = useCallback(async () => {
    try {
      setListing(await api<Listing>('/api/v1/backups/list'))
    } catch {
      // A listing that cannot be read is not an error worth a banner: the
      // card above already reports whether exports are landing at all.
      setListing({ backups: [], reason: 'the listing could not be read' })
    }
  }, [])

  const pollRun = useCallback(async () => {
    let next: RunState | null = null
    try {
      next = await api<RunState>('/api/v1/backups/runs')
    } catch {
      next = { state: 'unknown', reason: 'the job status could not be read' }
    }
    setRun(next)
    if (next?.state === 'running') {
      // Poll while it runs. A full export takes tens of minutes, so 10s is
      // frequent enough to feel live and rare enough to be free.
      timer.current = setTimeout(() => { void pollRun() }, 10_000)
    } else if (next?.state === 'succeeded') {
      void loadListing()
    }
  }, [loadListing])

  useEffect(() => {
    void loadListing()
    void pollRun()
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [loadListing, pollRun])

  const start = async () => {
    setConfirming(false)
    setBusy(true)
    setError(null)
    try {
      await api('/api/v1/backups/run', { method: 'POST', body: JSON.stringify({ reason }) })
      setRun({ state: 'running' })
      void pollRun()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The backup could not be started.')
    } finally {
      setBusy(false)
    }
  }

  const download = async (object: string) => {
    setError(null)
    try {
      // The server refuses without a reason: a signed URL to a full export
      // is an audited act, same as starting one.
      const why = window.prompt('Downloading a database export is recorded '
        + 'in the audit log. Why do you need it?')
      if (!why || !why.trim()) return
      const { url } = await api<{ url: string }>(
        `/api/v1/backups/download?object=${encodeURIComponent(object)}`
        + `&reason=${encodeURIComponent(why.trim())}`)
      // Navigate rather than fetch: the file is gigabytes and belongs to the
      // browser's download manager, not to this tab's memory.
      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The download link could not be created.')
    }
  }

  const running = run?.state === 'running'

  /* The environment cannot reach the export bucket at all — no project id, so
     neither the job nor the listing can be addressed. Derived from the two
     reads agreeing rather than from a separate flag, because the server is
     what knows, and a third source of truth would be one more thing to keep
     in step. */
  const unavailable =
    run?.state === 'unknown' && listing?.backups.length === 0 && listing.reason
      ? listing.reason
      : null

  return (
    <section className="st-section">
      <h2 className="st-h2">Backup controls</h2>

      {/* One cause, stated once. When the environment cannot reach the export
          bucket at all, the run state and the listing BOTH report it — the
          panel used to print "no GCP_PROJECT_ID in this environment" twice,
          once as a run status and once as an empty-list reason, which reads
          as two problems. */}
      {unavailable ? (
        <>
          <div className="panel rz-absent">
            Backups cannot be read or started here — {unavailable}. This is an
            environment configuration gap, not a failed export: the nightly job
            and its bucket are unaffected, and nothing on this panel can reach
            them until it is set.
          </div>
        </>
      ) : (
        <>
          <div className="st-grid">
            <Row label="last run"
                 value={
                   run === null ? '…'
                   : run.state === 'running' ? 'RUNNING — started ' + when(run.started_at)
                   : run.state === 'succeeded' ? `succeeded ${when(run.completed_at)}`
                   : run.state === 'failed' ? `FAILED — ${when(run.completed_at)}`
                   : run.state === 'none' ? 'this job has never been run by hand'
                   : `unknown — ${run.reason ?? 'could not be checked'}`
                 }
                 ok={run?.state === 'succeeded' ? true
                     : run?.state === 'failed' ? false : undefined} />
          </div>

          {!confirming ? (
            <button className="adm-btn" disabled={busy || running}
                    onClick={() => setConfirming(true)}>
              {running ? 'Backup running…' : 'Create backup'}
            </button>
          ) : (
            <div className="panel bk-confirm">
              <p>
                This exports <strong>both databases</strong> — the webapp database and
                the games database — to the environment&rsquo;s export bucket. It commonly
                takes <strong>30 minutes or more</strong>, and it runs whether or not
                you stay on this page.
              </p>
              <p className="muted">
                Both acts are recorded in the audit log with your reason.
              </p>
              <div className="bk-actions">
                <input className="adm-input" placeholder="Reason (required)"
                       value={reason} onChange={(e) => setReason(e.target.value)} />
                <button className="adm-btn" onClick={() => { void start() }}
                        disabled={busy || !reason.trim()}>
                  Yes, start the backup
                </button>
                <button className="adm-btn adm-btn--quiet"
                        onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {error && <p className="panel st-alarm">{error}</p>}

      <h3 className="rz-h3">Available exports</h3>
      {listing === null ? (
        <p className="muted rz-note">Reading the bucket…</p>
      ) : listing.backups.length === 0 ? (
        <p className="muted rz-note">
          {unavailable
            ? 'Nothing can be listed until the configuration above is set.'
            : `No exports are listed${listing.reason ? ` — ${listing.reason}` : ''}. `
              + 'Exports rotate after 7 days, so an empty list is expected on a '
              + 'new environment.'}
        </p>
      ) : (
        <table className="rz-table">
          <thead>
            <tr>
              <th style={{ width: '9rem' }}>Date</th>
              <th>File</th>
              <th style={{ width: '7rem' }}>Size</th>
              <th style={{ width: '14rem' }}>Written</th>
              <th style={{ width: '8rem' }} />
            </tr>
          </thead>
          <tbody>
            {listing.backups.map((day) => day.files.map((file, i) => (
              <tr key={day.date + file.name}>
                <td className="t-data">{i === 0 ? day.date : ''}</td>
                <td className="mono">{file.name}</td>
                <td className="muted">{size(file.size)}</td>
                <td className="muted">{when(file.updated)}</td>
                <td>
                  <button className="adm-btn adm-btn--quiet"
                          onClick={() => { void download(file.object) }}>
                    Download
                  </button>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/** Local copy of the Status page's label/value row, so this panel can be
 *  dropped in without exporting that page's internals.
 *
 *  The class names are st-row__label / st-row__value — the ones status.css
 *  actually defines. This copy used st-label / st-value, which are defined
 *  nowhere, so the label and the value rendered as two lines of unstyled body
 *  text inside a bare box. */
function Row({ label, value, ok }: {
  label: string; value: string; ok?: boolean
}) {
  const state = ok === undefined ? '' : ok ? ' is-ok' : ' is-bad'
  return (
    <div className={`st-row${state}`}>
      <span className="st-row__label t-label">{label}</span>
      <span className={`st-row__value${state}`}>{value}</span>
    </div>
  )
}
